import styles from './marketing-spot.module.css';
import { memo } from 'react';
import { useClubState } from '~/features/club/hooks/useClubState';
import { MarketingSpot as MarketingSpotType } from '~/lib/data-contract';
import { CategoryCardMedia, CategoryCardText, CMSLink } from '~/shared/components';
import { useIsInitialRenderState } from '~/shared/hooks/useIsInitialRender/useIsInitialRenderState';
import { useTranslation } from '~/shared/utils/translation/hooks/useTranslation';
import { clsx } from 'clsx';
import { queriesIsolated } from '~/theme';

export type MarketingSpotComponentProps = {
    marketingSpot: MarketingSpotType;
    index: number;
};

export const MarketingSpot = memo(({ marketingSpot, index }: MarketingSpotComponentProps) => {
    const {
        column,
        digizuiteImage,
        headline,
        subHeadline,
        callToAction,
        signupCallToAction,
    } = marketingSpot;

    const { isInitialRender } = useIsInitialRenderState();
    const { toggleSignUpActive, toggleSignUpActiveKeepScroll } = useClubState();
    const { translate } = useTranslation();

    const hasTextBlock = Boolean(headline || subHeadline);
    const callToActionUrl = signupCallToAction ? '' : callToAction?.url || ''; // ignore callToAction if signupCallToAction is true
    const isDouble = column === 'Double';

    const sizes = `${queriesIsolated.xs} 250px, ${queriesIsolated.sm} 310px, ${queriesIsolated.md} 330px, ${queriesIsolated.lg} 400px, ${queriesIsolated.xl} 500px`;

    const sizesDouble = `${queriesIsolated.xs} 500px, ${queriesIsolated.sm} 620px, ${queriesIsolated.md} 660px, ${queriesIsolated.lg} 800px, ${queriesIsolated.xl} 1000px`;

    return (
        <CMSLink
            className={clsx(styles.root, { [styles.double]: isDouble })}
            href={!signupCallToAction ? callToActionUrl : undefined}
            onClick={
                signupCallToAction
                    ? (e) => {
                          e.preventDefault();
                          toggleSignUpActive(true);
                          toggleSignUpActiveKeepScroll(true);
                      }
                    : undefined
            }
            title={signupCallToAction ? translate('club.signUp.dialogHeader'): callToAction?.title}
        >
            <div
                className={clsx(
                    styles.imageWrapper,
                    { [styles.double]: isDouble },
                    { [styles.relative]: hasTextBlock || signupCallToAction }
                )}
            >
                {digizuiteImage?.src && (
                    <CategoryCardMedia
                        image={digizuiteImage}
                        loading={
                            isInitialRender && index !== undefined && index < 8 ? 'eager' : 'lazy'
                        }
                        imgSizes={isDouble ? sizesDouble : sizes}
                    />
                )}
            </div>

            {hasTextBlock && !signupCallToAction && (
                <CategoryCardText
                    headline={headline}
                    text={subHeadline}
                    withLink={Boolean(callToAction)}
                    isDarkShade
                    lineClampText={3}
                    lineClampHeadline={2}
                    className={styles.cardText}
                />
            )}
            {signupCallToAction && (
                <CategoryCardText
                    headline={headline}
                    text={subHeadline}
                    lineClampText={3}
                    lineClampHeadline={2}
                    withLink
                    className={styles.cardText}
                    isDarkShade
                />
            )}
        </CMSLink>
    );
});
